import React, { useRef } from "react";
import { Typography, Box, Button, Grid, TextField, Chip, Autocomplete, Divider } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Signature from "../../../../common/signature/signature";
import PhoneInput from "react-phone-input-2";

const genders = ["Others", "Male", "Female"]; // Gender options
const languages = ["English", "Tamil", "Sinhalam"];
const legalStatuses = ["Status1", "Status2", "Status3"];

// Define validation schema
const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  previousNames: Yup.string().required("Previous Name is required"),
  gender: Yup.string().required("Gender is required"),
  dateOfBirth: Yup.date().required("Date of Birth is required"),
  religion: Yup.string().required("Religion is required"),
  ethnicity: Yup.string().required("Ethnicity is required"),
  language: Yup.string().required("Language is required"),
  legalStatus: Yup.string().required("Legal Status is required"),
  address: Yup.string().required("Address is required"),
  fatherName: Yup.string().required("Father's Name is required"),
  fatherAddress: Yup.string().required("Father's Address is required"),
  fatherMobile: Yup.string().required("Father's Mobile is required"),
  fatherReligion: Yup.string().required("Father's Religion is required"),
  motherName: Yup.string().required("Mother's Name is required"),
  motherAddress: Yup.string().required("Mother's Address is required"),
  motherMobile: Yup.string().required("Mother's Mobile is required"),
  motherReligion: Yup.string().required("Mother's Religion is required"),
  socialWorkerName: Yup.string().required("Social Worker's Name is required"),
  socialWorkerAddress: Yup.string().required("Social Worker's Address is required"),
  socialWorkerMobile: Yup.string().required("Social Worker's Mobile is required"),
  socialWorkerEmail: Yup.string().email("Invalid email format").required("Social Worker's Email is required"),
  placingAuthorityName: Yup.string().required("Placing Authority's Name is required"),
  placingAuthorityAddress: Yup.string().required("Placing Authority's Address is required"),
  placingAuthorityMobile: Yup.string().required("Placing Authority's Mobile is required"),
});

const AdmissionForm = () => {
  const signatureRef = useRef(null);

  const initialValues = {
    name: "",
    previousNames: "",
    gender: "",
    dateOfBirth: "",
    religion: "",
    ethnicity: "",
    language: "",
    legalStatus: "",
    address: "",
    fatherName: "",
    fatherAddress: "",
    fatherMobile: "",
    fatherReligion: "",
    motherName: "",
    motherAddress: "",
    motherMobile: "",
    motherReligion: "",
    socialWorkerName: "",
    socialWorkerAddress: "",
    socialWorkerMobile: "",
    socialWorkerEmail: "",
    placingAuthorityName: "",
    placingAuthorityAddress: "",
    placingAuthorityMobile: "",
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const signatureData = signatureRef.current?.getSignatureData();
        if (!signatureData) {
          alert("Please provide a signature");
          return;
        }

        const formData = { ...values, signature: signatureData };
        console.log("Form data submitted:", formData);
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
        <Form className='admission-form'>
          <Box>
            <Chip label='Admission' />
            <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "black" }} align='left' mt={1}>
              Admission Form
            </Typography>
            <Divider />
          </Box>

          {/* Young Person's Information */}
          <Typography sx={{ fontSize: "18px", color: "black" }} align='left' mt={4}>
            Young Person Information
          </Typography>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={5}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Name
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Name'
                name='name'
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Previous Names
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Previous Names'
                name='previousNames'
                value={values.previousNames}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                id='gender'
                options={genders}
                value={values.gender}
                onChange={(event, newValue) => setFieldValue("gender", newValue)}
                renderInput={(params) => (
                  <>
                    <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                      Gender
                    </Typography>
                    <TextField
                      {...params}
                      placeholder='Gender'
                      error={Boolean(touched.gender && errors.gender)}
                      helperText={touched.gender && errors.gender ? errors.gender : ""}
                    />
                  </>
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Date of Birth
              </Typography>
              <TextField
                fullWidth
                name='dateOfBirth'
                type='date'
                InputLabelProps={{ shrink: true }}
                value={values.dateOfBirth}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.dateOfBirth && Boolean(errors.dateOfBirth)}
                helperText={touched.dateOfBirth && errors.dateOfBirth}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Religion
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Religion'
                name='religion'
                value={values.religion}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.religion && Boolean(errors.religion)}
                helperText={touched.religion && errors.religion}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Ethnicity
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Ethnicity'
                name='ethnicity'
                value={values.ethnicity}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.ethnicity && Boolean(errors.ethnicity)}
                helperText={touched.ethnicity && errors.ethnicity}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                id='language'
                options={languages}
                value={values.language}
                onChange={(event, newValue) => setFieldValue("language", newValue)}
                renderInput={(params) => (
                  <>
                    <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                      Language
                    </Typography>
                    <TextField
                      {...params}
                      placeholder='Language'
                      error={Boolean(touched.language && errors.language)}
                      helperText={touched.language && errors.language ? errors.language : ""}
                    />
                  </>
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                id='legalStatus'
                options={legalStatuses}
                value={values.legalStatus}
                onChange={(event, newValue) => setFieldValue("legalStatus", newValue)}
                renderInput={(params) => (
                  <>
                    <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                      Legal Status
                    </Typography>
                    <TextField
                      {...params}
                      placeholder='Legal Status'
                      error={Boolean(touched.legalStatus && errors.legalStatus)}
                      helperText={touched.legalStatus && errors.legalStatus ? errors.legalStatus : ""}
                    />
                  </>
                )}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={10}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Address Prior to Entering the Home
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Previous Address'
                name='address'
                value={values.address}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.address && Boolean(errors.address)}
                helperText={touched.address && errors.address}
              />
            </Grid>
          </Grid>

          {/* Parent's Information */}
          <Typography sx={{ fontSize: "18px", color: "black" }} align='left' mt={4}>
            Parent’s Information
          </Typography>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "black" }} align='left' mt={1}>
            Father
          </Typography>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Name
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Name'
                name='fatherName'
                value={values.fatherName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.fatherName && Boolean(errors.fatherName)}
                helperText={touched.fatherName && errors.fatherName}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={10}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Address
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Address'
                name='fatherAddress'
                value={values.fatherAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.fatherAddress && Boolean(errors.fatherAddress)}
                helperText={touched.fatherAddress && errors.fatherAddress}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1} mb={1}>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Mobile Phone no
              </Typography>
              <PhoneInput
                country='gb'
                value={values.fatherMobile}
                onChange={(phone) => setFieldValue("fatherMobile", phone)}
                onBlur={handleBlur}
                inputProps={{
                  name: "fatherMobile",
                  required: true,
                }}
                containerStyle={{ width: "100%" }}
                inputStyle={{
                  width: "100%",
                  height: "55px",
                  fontSize: "16px",
                }}
              />
              {touched.fatherMobile && errors.fatherMobile && (
                <Typography color='error' variant='caption'>
                  {errors.fatherMobile}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Religious Persuasion
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Religion'
                name='fatherReligion'
                value={values.fatherReligion}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.fatherReligion && Boolean(errors.fatherReligion)}
                helperText={touched.fatherReligion && errors.fatherReligion}
              />
            </Grid>
          </Grid>

          <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "black" }} align='left' mt={3}>
            Mother
          </Typography>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Name
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Name'
                name='motherName'
                value={values.motherName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.motherName && Boolean(errors.motherName)}
                helperText={touched.motherName && errors.motherName}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={10}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Address
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Address'
                name='motherAddress'
                value={values.motherAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.motherAddress && Boolean(errors.motherAddress)}
                helperText={touched.motherAddress && errors.motherAddress}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Mobile Phone no
              </Typography>
              <PhoneInput
                country='gb'
                value={values.motherMobile}
                onChange={(phone) => setFieldValue("motherMobile", phone)}
                onBlur={handleBlur}
                inputProps={{
                  name: "motherMobile",
                  required: true,
                }}
                containerStyle={{ width: "100%" }}
                inputStyle={{
                  width: "100%",
                  height: "55px",
                  fontSize: "16px",
                }}
              />
              {touched.motherMobile && errors.motherMobile && (
                <Typography color='error' variant='caption'>
                  {errors.motherMobile}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Religious Persuasion
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Religion'
                name='motherReligion'
                value={values.motherReligion}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.motherReligion && Boolean(errors.motherReligion)}
                helperText={touched.motherReligion && errors.motherReligion}
              />
            </Grid>
          </Grid>

          {/* Social Worker/Placing Authority */}
          <Typography sx={{ fontSize: "18px", color: "black" }} align='left' mt={4}>
            Social Worker/Placing Authority
          </Typography>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Name of Social Worker
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Name'
                name='socialWorkerName'
                value={values.socialWorkerName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.socialWorkerName && Boolean(errors.socialWorkerName)}
                helperText={touched.socialWorkerName && errors.socialWorkerName}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Social Worker Address
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Address'
                name='socialWorkerAddress'
                value={values.socialWorkerAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.socialWorkerAddress && Boolean(errors.socialWorkerAddress)}
                helperText={touched.socialWorkerAddress && errors.socialWorkerAddress}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Mobile Phone no
              </Typography>
              <PhoneInput
                country='gb'
                value={values.socialWorkerMobile}
                onChange={(phone) => setFieldValue("socialWorkerMobile", phone)}
                onBlur={handleBlur}
                inputProps={{
                  name: "socialWorkerMobile",
                  required: true,
                }}
                containerStyle={{ width: "100%" }}
                inputStyle={{
                  width: "100%",
                  height: "55px",
                  fontSize: "16px",
                }}
              />
              {touched.socialWorkerMobile && errors.socialWorkerMobile && (
                <Typography color='error' variant='caption'>
                  {errors.socialWorkerMobile}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Email
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Email'
                name='socialWorkerEmail'
                type='email'
                value={values.socialWorkerEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.socialWorkerEmail && Boolean(errors.socialWorkerEmail)}
                helperText={touched.socialWorkerEmail && errors.socialWorkerEmail}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Placing Authority Name
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Name'
                name='placingAuthorityName'
                value={values.placingAuthorityName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.placingAuthorityName && Boolean(errors.placingAuthorityName)}
                helperText={touched.placingAuthorityName && errors.placingAuthorityName}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={10}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Placing Authority Address
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Address'
                name='placingAuthorityAddress'
                value={values.placingAuthorityAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.placingAuthorityAddress && Boolean(errors.placingAuthorityAddress)}
                helperText={touched.placingAuthorityAddress && errors.placingAuthorityAddress}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Placing Authority Mobile Phone
              </Typography>
              <PhoneInput
                country='gb'
                value={values.placingAuthorityMobile}
                onChange={(phone) => setFieldValue("placingAuthorityMobile", phone)}
                onBlur={handleBlur}
                inputProps={{
                  name: "placingAuthorityMobile",
                  required: true,
                }}
                containerStyle={{ width: "100%" }}
                inputStyle={{
                  width: "100%",
                  height: "55px",
                  fontSize: "16px",
                }}
              />
              {touched.placingAuthorityMobile && errors.placingAuthorityMobile && (
                <Typography color='error' variant='caption'>
                  {errors.placingAuthorityMobile}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Signature */}
          <Box className='signature-box'>
            <Signature ref={signatureRef} />
            <Typography align='right'>
              <Grid container>
                <Grid>
                  <Typography sx={{ fontSize: "14px", color: "#FFB700" }}>Venorox </Typography>
                </Grid>
                <Grid>
                  <Grid><Typography sx={{ fontSize: "14px" }}>/ Signature</Typography></Grid>
                </Grid>
              </Grid>
              <Typography sx={{ fontSize: "14px" }}>2024/03/12</Typography>
              <Grid className='clear-signature-button'>
                <Button variant='outlined' onClick={() => signatureRef.current?.clearSignature()}>
                  Clear
                </Button>
              </Grid>
            </Typography>
          </Box>

          {/* Submit button */}
          <Grid item xs={12} mt={2}>
            <div className='submit-button-container'>
              <Button type='submit' variant='contained' color='primary' className='submit-button'>
                Submit
              </Button>
            </div>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default AdmissionForm;
